import { Tabs, TabsContent, TabsList, TabsTrigger } from '@repo/ui/components/tabs';
import { useTranslation } from 'react-i18next';
import ConsignHouse from '../__components/consign-house';
import FormFindHouse from '../__components/form-find-house';
import SectionChild from '~/components/SectionChild';
import Heading2 from '../__components/heading2';
import { Button } from '@repo/ui/components/button';
import { ArrowRight, ChevronDown, ChevronUp } from '@repo/ui/components/icon';
import Carousel from '~/components/ui/carousel';
import { CarouselItem } from '@repo/ui/components/carousel';
import { Card, CardRealEstate, CardRealEstateMini, CardThumbnail, MiniCard } from '~/components/card';
import FormRecruitment from '../__components/form-recruitment';
import { Await, Link, useLoaderData } from 'react-router';
import { cn } from '@repo/ui/lib/utils';
import { useMemo, useState } from 'react';
import { buildCanonicalLink, buildMetaFromLoader, buildSeoMeta, getOrigin } from '~/utils/seo';
import type { Route } from './+types/_index';
import SectionContainer from '~/components/section-container';
import { clientCallAPI } from '~/services/clientCallAPI';
import { serverCallAPI, serverCallApiCMS } from '~/services/serverCallAPI';
import { FeaturedProject, ILandingPage, IPage, ProductListingResponse } from '~/types/types';
import { hostName, top3NewestFromGroups } from '~/utils/format';
import MainBanner from '~/components/main-banner';
import React from 'react';
import { StructuredDataScript } from '~/components/structured-data-script';
import { FAKE_PRODUCTS } from '~/data/fake-products';
import { Skeleton } from '@repo/ui/components/skeleton';

const featuredImage = [
  [
    {
      url: "/image/landing_page/feature-image/bg-1.jpg",
    },
    {
      url: "/image/landing_page/feature-image/bg-2.jpg",
    },
  ],
  [
    {
      url: "/image/landing_page/feature-image/bg-3.jpg",
    },
    {
      url: "/image/landing_page/feature-image/bg-4.jpg",
    },
  ],
  [
    {
      url: "/image/landing_page/feature-image/bg-5.jpg",
    },
    {
      url: "/image/landing_page/feature-image/bg-6.jpg",
    },
  ],
  [
    {
      url: "/image/landing_page/feature-image/bg-7.jpg",
    },
    {
      url: "/image/landing_page/feature-image/bg-8.jpg",
    },
  ],
];

export { action } from "./__form.server";
export async function loader({ request }: Route.LoaderArgs) {

  const config: ILandingPage = await serverCallApiCMS<ILandingPage>(
    "/api/v1/landing-page/config"
  ).catch(() => {
    return {
      news: [],
      project: [],
      config: {
        hero: [],
        about: { description: "" }
      },
    };
  });
  const contentPage = await serverCallApiCMS<IPage>(
    "/api/v1/web/pages/trang-chu"
  ).catch(() => {
    return {
      title: "",
      slug: "",
      description: "",
      content: "",
      seo: {
        title: "",
        meta_description: "",
        focus_keyphrase: "",
        og_image: [],
      },
      structured: null,
      structuredData: null,
    };
  });

  const data: ProductListingResponse =
    await clientCallAPI<ProductListingResponse>("/api/featured-product").catch(
      () => {
        return { productWithProvinceId: [], productHot: [], totalCount: 0 };
      }
    );

  const featuredProjects = serverCallAPI<FeaturedProject[]>(
    "/api/project/top"
  ).catch(() => {
    return [];
  });
  const productHot =
    data.productHot && data.productHot.length > 0
      ? data.productHot
      : FAKE_PRODUCTS.slice(0, 6);
  const productWithProvinceId =
    data.productWithProvinceId && data.productWithProvinceId.length > 0
      ? data.productWithProvinceId
      : [
        {
          provinceId: "fake",
          provinceName: "Tuyên Quang",
          products: FAKE_PRODUCTS.slice(0, 4),
        },
      ];
  const origin = getOrigin(request);

  const canonical = origin;

  return {
    config,
    ...data,
    productWithProvinceId,
    productHot,
    featuredProjects,
    seo: {
      canonical,
      ...contentPage.seo,
    },
    structuredData: contentPage.structuredData ?? null,
  };
}

const FEATURED_PROJECT_ITEM_CLASS =
  "max-w-[310px] min-h-[420px] md:max-w-[410px] md:min-h-[520px]";

function FeaturedProjectsSkeletonItems({ count = 6 }: { count?: number }) {
  return Array.from({ length: count }).map((_, idx) => (
    <CarouselItem
      className={FEATURED_PROJECT_ITEM_CLASS}
      key={`featured-project-skeleton-${idx}`}
    >
      <div className="relative block w-full h-full">
        <div className="h-full">
          <div className="relative w-full h-full overflow-hidden rounded-2xl">
            <Skeleton className="absolute inset-0 w-full h-full" />
            <div className="absolute bottom-0 w-full rounded-md h-2/3 bg-grad-mix"></div>
          </div>
          <div className="absolute bottom-0 left-0 w-full px-4 py-5 text-white">
            <Skeleton className="w-4/5 h-6 bg-white/30" />
            <div className="h-px my-4 bg-primary-300" />
            <Skeleton className="w-full h-4 bg-white/20" />
            <Skeleton className="w-3/4 h-4 mt-2 bg-white/20" />
          </div>
        </div>
      </div>
    </CarouselItem>
  ));
}

function FeaturedProjectsFromApi({ projects }: { projects: FeaturedProject[] }) {
  const getProjectDescription = (item: FeaturedProject) =>
    item.description ||
    [item.wardName, item.provinceName].filter(Boolean).join(", ") ||
    item.address ||
    "";

  const getProjectImages = (item: FeaturedProject) =>
    item.thumbnailUrls?.length
      ? item.thumbnailUrls
      : item.thumbnails?.map((thumb) => thumb.fileUrl || "");

  return projects.map((item) => (
    <CarouselItem className={FEATURED_PROJECT_ITEM_CLASS} key={item.id}>
      <CardThumbnail
        title={item.name}
        description={getProjectDescription(item)}
        imageUrls={getProjectImages(item)}
        href={`/du-an/${item.slug}`}
      />
    </CarouselItem>
  ));
}

function FeaturedProjectsFromConfig({
  projects,
}: {
  projects: ILandingPage["project"];
}) {
  return projects.map((item) => (
    <CarouselItem className={FEATURED_PROJECT_ITEM_CLASS} key={item.id}>
      <CardThumbnail
        title={item.title}
        description={item.description}
        imageUrls={item.thumbnail?.map((thumb) => hostName(thumb.path))}
        href={`/tin-tuc/${item.slug}`}
      />
    </CarouselItem>
  ));
}

function FeaturedImage() {
  const { t } = useTranslation("home");

  return (
    <SectionChild className="py-12">
      <Heading2>{t("featured_image.label")}</Heading2>
      <Carousel className="py-12"
        propsContent={{
          className: "gap-5"
        }}
      >
        {featuredImage.map((images, idx) => {
          return (
            <CarouselItem className="max-w-[282px]" key={idx}>
              <div className='space-y-5'>
                {images.map((i, index) => {
                  return (
                    <Link
                      to={i.url || "#"}
                      key={index}
                      className={cn(
                        "relative block w-[282px] drop-shadow-xs rounded-xl overflow-hidden",
                        (idx % 2 === 0 && index % 2 === 0) ||
                          (idx % 2 !== 0 && index % 2 !== 0)
                          ? "h-[282px]"
                          : "h-[220px]"
                      )}
                    >
                      <img
                        className={cn("object-cover absolute w-full h-full")}
                        src={i.url}
                        alt="featured-image"
                      />
                    </Link>
                  );
                })}
              </div>
            </CarouselItem>
          );
        })}
      </Carousel>
    </SectionChild>
  );
}
export function FormSearch() {
  const { t } = useTranslation("home");
  return (
    <div className="max-w-[1000px] bg-brand-500 mx-auto rounded-2xl">
      <Tabs className="p-9" defaultValue="ki-gui-nha">
        <TabsList className="h-[44px]">
          <TabsTrigger
            value="ki-gui-nha"
            className="text-[#787879] h-10 p-4 data-[state=active]:text-white data-[state=active]:bg-primary"
          >
            {t("search_box.tabs.consign")}
          </TabsTrigger>
          <TabsTrigger
            value="tim-nha"
            className="text-[#787879] p-4 h-10 data-[state=active]:text-white
            data-[state=active]:bg-primary
            "
          >
            {t("search_box.tabs.find")}
          </TabsTrigger>
        </TabsList>
        <TabsContent value="ki-gui-nha">
          <ConsignHouse />
        </TabsContent>
        <TabsContent value="tim-nha">
          <FormFindHouse />
        </TabsContent>
      </Tabs>
    </div>
  );
}
function FeaturedProjects() {
  const { t } = useTranslation("home");
  const data = useLoaderData<typeof loader>();

  const renderSection = (items: React.ReactNode, viewAllHref: string) => (
    <div className="pb-20 pt-[100px] bg-brand-50">
      <SectionChild>
        <div className="flex items-center justify-between gap-x-4">
          <Heading2>{t("featured_projects.title")}</Heading2>
          <Button variant={"outline"} size={"lg"} asChild>
            <Link to={viewAllHref}>
              {t("featured_projects.actions.view_all")}
              <ArrowRight />
            </Link>
          </Button>
        </div>
      </SectionChild>
      <SectionContainer>
        <div className="py-7">
          <Carousel className="gap-6 pb-8">{items}</Carousel>
        </div>
      </SectionContainer>
    </div>
  );

  return (
    <React.Suspense fallback={renderSection(<FeaturedProjectsSkeletonItems count={6} />, "/du-an")}>
      <Await
        resolve={data.featuredProjects}
        errorElement={
          data.config.project.length > 0
            ? renderSection(<FeaturedProjectsFromConfig projects={data.config.project} />, "/tin-tuc")
            : null
        }
      >
        {(featuredProjects) => {
          if (featuredProjects.length > 0) {
            return renderSection(<FeaturedProjectsFromApi projects={featuredProjects} />, "/du-an");
          }

          if (data.config.project.length > 0) {
            return renderSection(<FeaturedProjectsFromConfig projects={data.config.project} />, "/tin-tuc");
          }

          return null;
        }}
      </Await>
    </React.Suspense>
  );
}

function RealEstateNews() {
  const { t, } = useTranslation("home")
  const { t: common } = useTranslation("common");
  const { productWithProvinceId } = useLoaderData<typeof loader>();

  const listTab = useMemo(() => {
    const newData = productWithProvinceId.map((item) => {
      return {
        label: item.provinceName,
        value: item.provinceName,
        products: item.products,
      };
    })
    return [
      {
        label: common("dropdown.sale.label.all"),
        value: "all",
        products: top3NewestFromGroups(productWithProvinceId),
      },
    ].concat(newData);
  }, [])
  return (
    <div className="pt-10">
      <SectionChild>
        <div className="flex items-center justify-between mt-10">
          <Heading2>{t("latest_listings.title")}</Heading2>

          <Button variant={"outline"} size={"lg"} asChild>
            <Link to="/mua-ban-mua-ban-can-ho">
              {t("latest_listings.actions.view_all")}
              <ArrowRight />
            </Link>
          </Button>
        </div>
      </SectionChild>
      <SectionContainer>
        <Tabs className="mt-5 gap-9" defaultValue="all">
          <TabsList className="h-[46px] rounded-none bg-transparent p-0">
            {listTab.map((item) => {
              return (
                <TabsTrigger
                  value={item.value}
                  className="h-10 p-4 rounded-none  data-[state=active]:text-primary data-[state=active]:bg-primary-50
                  data-[state=active]:border-b-2 data-[state=active]:border-primary
                  border-0 bg-none text-gray-500 "
                  key={item.value}
                >
                  <div>{item.label}</div>
                </TabsTrigger>
              );
            })}
          </TabsList>
          {listTab.map((item) => {
            return (
              <TabsContent value={item.value} key={item.value}>
                <div>
                  <Carousel className="gap-4 pb-8">
                    {item.products.map((product, index) => {
                      return (
                        <CarouselItem
                          className="max-w-[400px]
                        lg:max-w-full lg:basis-1/3"
                          key={index}
                        >
                          <CardRealEstate product={product} />
                        </CarouselItem>
                      );
                    })}
                  </Carousel>
                </div>
              </TabsContent>
            );
          })}
        </Tabs>
      </SectionContainer>
    </div>
  );
}
function Introduction() {
  const { t } = useTranslation("home");

  return (
    <SectionChild className="flex flex-col gap-5 py-24 md:flex-row md:items-center">
      <div className="pr-10 space-y-6">
        <Heading2>{t("introduction.label")}</Heading2>
        <p>{t("introduction.description")}</p>
        <Button size={"lg"} asChild>
          <Link to="/gioi-thieu">{t("introduction.label")}</Link>
        </Button>
      </div>
      <div className="">
        <img
          src="/image/introduction.webp"
          width={520}
          className="object-cover w-full rounded-2xl"
        />
      </div>
    </SectionChild>
  );
}
function News() {
  const { t } = useTranslation("home")
  const data = useLoaderData<typeof loader>();
  const array1 = data.config.news.slice(0, 4);
  const array2 = data.config.news.slice(4);
  return (
    <div className="bg-primary-50">
      <div className="py-20">
        <SectionChild>
          <div className="flex items-center justify-between pb-5">
            <Heading2>{t("news_section.title")}</Heading2>
            <Button variant={"outline"} size={"lg"} asChild>
              <Link to="/tin-tuc">
                {t("news_section.actions.view_all")}
                <ArrowRight />
              </Link>
            </Button>
          </div>
          <div className="flex flex-col gap-5 md:flex-row">
            <div className="w-full md:w-[560px]">
              <Carousel
                className="w-full pb-5"
                userOptions={{ delay: 5000, playOnInit: false }}
                opts={{
                  loop: true,
                }}
                showDot
              >
                {array1.map((item) => {
                  return (
                    <CarouselItem
                      className="w-full md:min-w-[560px] h-[320px] md:h-[440px]"
                      key={item.id}
                    >
                      <Card post={item} />
                    </CarouselItem>
                  );
                })}
              </Carousel>
            </div>
            <div className="flex-1 hidden grid-cols-2 gap-2 md:grid place-content-between">
              {(array2.length > 0 ? array2 : array1).map((item) => {
                return (
                  <div
                    className="w-full max-w-[300px] h-full min-h-[220px]"
                    key={item.id}
                  >
                    <MiniCard post={item} />
                  </div>
                );
              })}
            </div>
          </div>
        </SectionChild>
        <SectionContainer className="block md:hidden">
          <Carousel className="mt-3">
            {(array2.length > 0 ? array2 : array1).map((item, index) => {
              return (
                <CarouselItem
                  className="w-full max-w-[300px] h-[200px]"
                  key={index}
                >
                  <MiniCard post={item} />
                </CarouselItem>
              );
            })}
          </Carousel>
        </SectionContainer>
      </div>
    </div>
  );
}
function HotProduct() {
  const data = useLoaderData<typeof loader>();
  return (
    <div className='pb-[100px]'>
      <SectionChild className="bg-primary rounded-[20px]">
        <div className="py-10 md:p-10">
          <Heading2 className='text-white'>Sản phẩm nổi bật</Heading2>
          <Carousel className="gap-6 pb-8 mt-8">
            {data.productHot.map((product) => {
              return (
                <CarouselItem
                  className="max-w-[384px] min-h-[420px]"
                  key={product.id}
                >
                  <CardRealEstateMini product={product} hiddenButton />
                </CarouselItem>
              );
            })}
          </Carousel>
        </div>
      </SectionChild>
    </div>
  );
}
function ContentPage({ description }: ILandingPage["config"]["about"]) {
  const [open, setOpen] = useState(false)
  return (
    <div className="pb-10">
      <SectionChild className={" border-l-4 bg-[#F0F9F9] border-primary flex flex-col gap-4"}>
        <div
          className={cn(
            "py-10 overflow-hidden relative px-10 pb-10",
            open ? "h-full" : "h-[200px]"
          )}
        >
          {!open && (
            <div className="absolute w-full h-full bg-overlay-third"></div>
          )}

          <div
            className="max-w-full prose ck-content"
            dangerouslySetInnerHTML={{
              __html: description || "",
            }}
          ></div>
        </div>
        <div className="px-10 pb-10">
          <div
            className="flex items-center font-sans font-bold cursor-pointer text-primary"
            onClick={() => setOpen(!open)}
          >
            {open ? "Rút gọn" : "Xem thêm"}
            {open ? <ChevronUp /> : <ChevronDown />}
          </div>
        </div>
      </SectionChild>
    </div>
  );
}

function StatsSection() {
  const items: { value: string; label: string; plus?: boolean }[] = [
    { value: "10", label: "Năm thành lập" },
    { value: "5000", label: "Bất động sản", plus: true },
    { value: "1000", label: "Người ký gửi", plus: true },
    { value: "10", label: "Dự án độc quyền", plus: true },
  ];

  return (
    <section className="w-full pt-20 md:pt-16 ">
      <div className="px-4 mx-auto max-w-3xs md:max-w-7xl md:px-6">
        <Carousel
          className="w-full mt-5 md:hidden"
          userOptions={{ delay: 5000, playOnInit: true }}
          opts={{
            loop: true,
          }}
          scroll
        >
          {items.map((item, idx) => {
            return (
              <CarouselItem className="relative p-0" key={idx}>
                <div className="text-center text-primary">
                  <div className="inline-flex items-center px-10">
                    <div className="relative text-[64px] font-semibold leading-none md:text-6xl">
                      <span>{item.value}</span>
                    </div>
                    {item.plus ? (
                      <span className="ml-1 text-2xl leading-none align-middle md:text-3xl">
                        +
                      </span>
                    ) : null}
                  </div>
                  <div className="text-sm font-medium md:text-base">
                    {item.label}
                  </div>
                </div>
              </CarouselItem>
            );
          })}
        </Carousel>
        <div className="items-stretch justify-center hidden gap-8 md:flex md:flex-row md:items-center md:justify-between md:gap-0">
          {items.map((item, index) => (
            <React.Fragment key={item.label}>
              {index !== 0 && (
                <div
                  className="w-full md:w-[1px] h-px  md:h-[112px] bg-primary-100"
                  aria-hidden
                />
              )}
              <div
                key={item.label}
                className="flex items-center justify-center"
              >
                {/* Vertical divider (only show between cards on md+) */}

                {/* Stat card */}
                <div className="text-center text-primary">
                  <div className="inline-flex items-center px-10">
                    <span className="text-[64px] font-semibold leading-none md:text-6xl">
                      {item.value}
                    </span>
                    {item.plus ? (
                      <span className="ml-1 text-2xl leading-none align-middle md:text-3xl ">
                        +
                      </span>
                    ) : null}
                  </div>
                  <div className="text-sm font-medium md:text-base">
                    {item.label}
                  </div>
                </div>
              </div>
            </React.Fragment>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function Index() {
  const data = useLoaderData<typeof loader>();

  return (
    <>
      <StructuredDataScript data={data.structuredData} />
      <div className="relative">
        <MainBanner listBanner={data.config.config.hero} />
        <div className="absolute w-full -translate-y-1/2 top-full lg:block ">
          <div className="relative">
            <FormSearch />
          </div>
        </div>
      </div>
      <div className="bg-white pt-[100px] pb-10">
        <SectionChild>
          <StatsSection />
        </SectionChild>
      </div>
      <FeaturedProjects />
      <RealEstateNews />
      <HotProduct />
      <FormRecruitment />

      <div>
        <News />
        <Introduction />
        <FeaturedImage />
        <ContentPage description={data.config.config.about.description} />
      </div>
    </>
  );
}

export const meta = (args: Route.MetaArgs) => {
  return buildMetaFromLoader(
    args,
    {
      hostName,
      buildSeoMeta,
      siteName: "Kland",
      separator: "|",
    },
    (ld) => ld
  );

};

export const handle = {
  dynamicLinks: (props: any) => {
    return buildCanonicalLink(props?.data?.seo?.canonical || "");
  },
};
